import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {DocumentTextIcon as DocumentTextIconSolid, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {formattedDateTime} from "services/date";
import Dropdown from "components/Dropdown";
import {useNavigate} from "react-router-dom";
import SearchBar from "components/SearchBar";
import BooleanIndicator from "components/BooleanIndicator";
import {Note} from "types/Note";
import {DocumentTextIcon} from "@heroicons/react/outline";
import {toTitleCase, truncate} from "services/string";
import DeleteNote from '../Delete/DeleteNote';
import Button from "../../../components/Button";
import ShortcutModal from "../../../components/Shortcut/ShortcutModal";
import NoteForm from "../Form";
import {useStoreNote} from "../../../hooks/api/useStoreNote";
import {useUpdateNote} from "../../../hooks/api/useUpdateNote";
import {useOperator} from "../../../context/OperatorContext";

function NoteList() {
    const {termFor} = useOperator();
    const [search, setSearch] = useState<string|null>(null);
    const [page, setPage] = useState<number>(1);
    const [deletingNote, setDeletingNote] = useState<Note | null>(null);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState<boolean>(false);
    const [note, setNote] = useState<Note | null>(null);

    const result = useQuery(
        ['notes.all', {search, page}],
        () => ngage.notes.all({search, page}),
        {keepPreviousData: true}
    )

    const navigate = useNavigate();
    const storeNote = useStoreNote();
    const updateNote = useUpdateNote();

    // Prefetch next and previous pages
    prefetch('notes.all', {page, search}, ngage.notes.all);

    function openModal(note?: Note) {
      setIsNoteModalOpen(true);
      setNote(note ?? null);
    }

    function closeModal() {
      setIsNoteModalOpen(false);
      setNote(null);
    }

    const totalNotes: number = result.data?.meta.all ?? 0;

    // Prepare meta data
    const meta = [
        {icon: DocumentTextIconSolid, value: totalNotes.toLocaleString() + ` ${totalNotes === 1 ? 'note' : 'notes'}`},
    ]
    const actions = <Button icon={PlusIcon} onClick={() => openModal()}>New</Button>

    return (
        <Page title="Notes"
              subTitle={`Ancillary notes for ${termFor('Exhibits')}, ${termFor('Facilities')} and ${termFor('Trails')}, displayed in-app`}
              breadcrumbs={{'App Content': '/', 'Featured': '/', 'Notes': '/notes'}}
              meta={meta}
              actions={actions}
        >

            {/* Search and filtering */}
            <div className="mb-4 w-full grid md:grid-cols-4">
                <SearchBar isSearching={result.isFetching} onChange={setSearch} />
            </div>

            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Title</TableHeader>
                        <TableHeader>Excerpt</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.CENTER}>Important</TableHeader>
                        <TableHeader>Expires</TableHeader>
                        <TableHeader>Subject</TableHeader>
                        <TableHeader>Author</TableHeader>
                        <TableHeader>Written</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={8}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <DocumentTextIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No notes</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((note, index) => (
                        <TableRow key={note.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position="left">
                                            {[
                                                // {name: 'View', icon: EyeIcon, onClick: () => navigate(`/notes/${note.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => openModal(note)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingNote(note)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-500" /> {note.title}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{truncate(note.body, 30)}</TableData>
                            <TableData alignment={TableData.alignments.CENTER}>
                                <BooleanIndicator value={note.is_important} />
                            </TableData>
                            <TableData>{formattedDateTime(note.expires_at) || 'Never'}</TableData>
                            <TableData>
                                {!!note.notable? `${note.notable.name} (${toTitleCase(termFor(note.notable_type))})` : 'Unknown'}
                            </TableData>
                            <TableData>{note.author.name}</TableData>
                            <TableData>{formattedDateTime(note.created_at)}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        // {name: 'View', icon: EyeIcon, onClick: () => navigate(`/notes/${note.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => openModal(note)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingNote(note)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete confirmation modal */}
                            <DeleteNote
                                note={deletingNote}
                                show={note === deletingNote}
                                onCancel={() => setDeletingNote(null)}
                                onDelete={() => navigate('/notes')}
                                onDeleteFail={() => setDeletingNote(null)}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>

            <ShortcutModal show={isNoteModalOpen} close={closeModal}>
              <NoteForm
                note={note ?? undefined}
                onSubmit={async (values, helpers) => {
                  if (note && values.mode === 'update') {
                    await updateNote(note.id, values, helpers) && closeModal();
                  } else
                    await storeNote(values, helpers) && closeModal();
                  }
                }
                onCancel={() => closeModal()}
              />
            </ShortcutModal>
        </Page>
    );
}

export default NoteList;